import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { FootStatus, OverallDifficulty, RirValue } from "@jilian/shared";
import {
  calculateExerciseVolume,
  calculateWorkoutMetabolism,
  calculateDailyEnergyBalance,
  decideProgression,
  evaluateReadiness,
  toProgressionDecision,
  type ProgressionVerdict,
  type WorkoutExerciseResult,
  type WorkoutExercisePerformance,
} from "@jilian/core";
import { useSessionStore, type SessionExercise } from "../state/useSessionStore";
import { useAppStore } from "../state/useAppStore";
import { useSettings, useMeals, useMeasurements } from "../db/hooks";
import { EXERCISE_BY_ID } from "../data/exercises";
import { loadExerciseHistory } from "../db/workoutRepo";
import { saveProgressionDecision } from "../db/repo";
import { uuid } from "../lib/ids";
import { formatElapsed, formatKg, scopeShortLabel } from "../lib/format";
import { Button, Card, RiskTag, Segmented, Stat } from "../components/ui";

/** 动作基础重量：取最后一组完成记录的实际重量（口径随条目保存）。 */
function baseWeightKg(e: SessionExercise): number | null {
  const completed = e.sets.filter((s) => s.completed && s.weightKg != null);
  const last = completed[completed.length - 1];
  return last?.weightKg ?? null;
}

/** 由会话数据构建规则引擎输入（进阶建议全部由 @jilian/core 确定性计算）。 */
function toExerciseResult(
  e: SessionExercise,
  planType: WorkoutExerciseResult["planType"],
): WorkoutExerciseResult {
  const completed = e.sets.filter((s) => s.completed);
  const all = e.sets;
  return {
    exerciseId: e.exerciseId,
    weightKg: baseWeightKg(e),
    weightUnitScope: e.weightUnitScope,
    targetSets: e.targetSets,
    repMin: e.repMin,
    repMax: e.repMax,
    targetRir: e.targetRir,
    failureAllowedLastSet: e.allowFailureLastSet,
    hasCompleteData: e.sets.length >= e.targetSets && e.sets.every((s) => s.completed && s.reps != null && s.rir != null),
    jointPain: completed.some((s) => s.jointPain),
    injuryPain: completed.some((s) => s.injuryPain),
    formBreakdown: all.some((s) => s.formBreakdown),
    planType,
    restShort: all.some((s) => s.actualRestSeconds != null && s.actualRestSeconds < e.restSeconds * 0.6),
    skipped: e.status === "skipped",
    endedEarly: e.status === "ended_early",
    sets: all.map((s, i) => ({
      reps: s.reps ?? 0,
      rir: (s.rir ?? 4) as RirValue,
      isLast: i === all.length - 1,
    })),
  };
}

const DECISION_TAG: Record<ProgressionVerdict["decision"], { label: string; tone: "good" | "warn" | "danger" | "accent" }> = {
  increase: { label: "下次加重", tone: "good" },
  maintain: { label: "维持重量", tone: "accent" },
  decrease: { label: "下次降重", tone: "warn" },
  recalibrate: { label: "试重评估", tone: "warn" },
  replace: { label: "建议替换", tone: "danger" },
  no_decision: { label: "暂不判定", tone: "accent" },
};

const DIFF_OPTIONS: { value: OverallDifficulty; label: string }[] = [
  { value: "easy", label: "轻松" },
  { value: "appropriate", label: "合适" },
  { value: "very_hard", label: "很累" },
];

interface ExerciseVerdict {
  e: SessionExercise;
  verdict: ProgressionVerdict;
  volume: number | null;
}

export function WorkoutSummary() {
  const nav = useNavigate();
  const store = useSessionStore();
  const settings = useSettings();
  const userId = settings?.profile.id ?? store.userId ?? "user-default";
  const setLastReadiness = useAppStore((s) => s.setLastReadiness);
  const meals = useMeals(userId);
  const measurements = useMeasurements(userId);

  const [difficulty, setDifficulty] = useState<OverallDifficulty>("appropriate");
  const [foot, setFoot] = useState<FootStatus>("stable");
  const [verdicts, setVerdicts] = useState<ExerciseVerdict[]>([]);
  const [overrideInput, setOverrideInput] = useState<Record<string, string>>({});
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  /** 保存本次进阶建议为决策记录（保留原建议 + 用户覆盖重量，§32.1 #14）。 */
  const saveVerdict = async (exerciseId: string, verdict: ProgressionVerdict, overrideKg?: string) => {
    if (!store.sessionId) return;
    const override = overrideKg?.trim();
    const parsed = override ? Number(override) : null;
    const decision = toProgressionDecision(verdict, {
      id: uuid(),
      userId,
      exerciseId,
      sourceSessionId: store.sessionId,
      createdAt: new Date().toISOString(),
      userAccepted: true,
      userOverrideWeightKg: parsed != null && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    });
    await saveProgressionDecision(decision);
    setSavedIds((prev) => new Set(prev).add(exerciseId));
  };

  /** 一键采纳全部进阶建议 */
  const handleBatchAccept = async () => {
    for (const { e, verdict } of verdicts) {
      if (!savedIds.has(e.exerciseId)) {
        await saveVerdict(e.exerciseId, verdict, overrideInput[e.exerciseId]);
      }
    }
  };

  // 规则引擎进阶建议计算
  useEffect(() => {
    const sessionId = store.sessionId;
    const exercises = store.exercises;
    const planType = store.planType;
    const readiness =
      store.readinessSnapshot ??
      evaluateReadiness({
        sleepQuality: "good",
        fatigueLevel: "low",
        muscleSoreness: "none",
        footStatus: "stable",
        jointPain: "none",
      });
    if (!sessionId || exercises.length === 0) return;
    let mounted = true;

    void (async () => {
      const rows: ExerciseVerdict[] = [];
      for (const e of exercises) {
        const ex = EXERCISE_BY_ID.get(e.exerciseId);
        if (!ex) continue;
        const history = await loadExerciseHistory(e.exerciseId, sessionId);
        const current = toExerciseResult(e, planType);
        const verdict = decideProgression({ exercise: ex, currentSession: current, history, readiness });
        const sets = e.sets.filter((s) => s.completed && s.weightKg != null && s.reps != null);
        let volume: number | null = null;
        if (sets.length && e.weightUnitScope !== "assistance") {
          try {
            volume = calculateExerciseVolume(
              sets.map((s) => ({ actualWeightKg: s.weightKg as number, reps: s.reps as number })),
              e.weightUnitScope,
            );
          } catch {
            volume = null;
          }
        }
        if (mounted) rows.push({ e, verdict, volume });
      }
      if (mounted) setVerdicts(rows);
    })();

    return () => {
      mounted = false;
    };
  }, [store.sessionId, store.exercises, store.planType, store.readinessSnapshot]);

  const elapsed = store.startedAt && store.finishedAt ? Math.floor((store.finishedAt - store.startedAt) / 1000) : 0;
  const doneSets = useMemo(
    () => store.exercises.reduce((acc, e) => acc + e.sets.filter((s) => s.completed).length, 0),
    [store.exercises],
  );

  // 科学能量代谢与单场消耗计算
  const metabolism = useMemo(() => {
    const currentWeight = measurements && measurements.length > 0 ? measurements[measurements.length - 1]!.weightKg : 80;
    const perfExercises: WorkoutExercisePerformance[] = store.exercises.map((e) => {
      const ex = EXERCISE_BY_ID.get(e.exerciseId);
      return {
        exerciseId: e.exerciseId,
        nameZh: ex?.nameZh ?? e.exerciseId,
        primaryMuscles: ex?.primaryMuscles ?? [],
        compoundType: ex?.compoundType ?? "isolation",
        weightUnitScope: e.weightUnitScope,
        sets: e.sets.map((s) => ({
          weightKg: s.weightKg,
          reps: s.reps,
          rir: s.rir,
          completed: s.completed,
        })),
      };
    });
    const durationMins = Math.max(15, Math.round(elapsed / 60));
    return calculateWorkoutMetabolism({
      bodyWeightKg: currentWeight,
      durationMinutes: durationMins,
      exercises: perfExercises,
    });
  }, [measurements, store.exercises, elapsed]);

  // 全天净能量平衡与减脂成效
  const dailyBalance = useMemo(() => {
    const currentWeight = measurements && measurements.length > 0 ? measurements[measurements.length - 1]!.weightKg : 80;
    const list = meals ?? [];
    const intakeCalories = list.reduce((acc, m) => acc + (m.finalCalories ?? 0), 0);
    const intakeProtein = list.reduce((acc, m) => acc + (m.finalProtein ?? 0), 0);
    return calculateDailyEnergyBalance({
      bodyWeightKg: currentWeight,
      intakeCalories,
      intakeProteinGrams: intakeProtein,
      workoutBurnCalories: metabolism.totalCalorieBurn,
    });
  }, [measurements, meals, metabolism]);

  const allSaved = verdicts.length > 0 && verdicts.every((v) => savedIds.has(v.e.exerciseId));

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-12 space-y-4">
      <header className="pt-2">
        <h1 className="text-[26px] font-bold text-tone-ink tracking-tight font-title">训练结算与总结</h1>
        <div className="text-[12px] font-medium text-tone-soft mt-0.5 font-caption">
          序列 {store.sequence} · 训练时长 {formatElapsed(elapsed)} · 完成 {doneSets} 组
        </div>
      </header>

      {/* 核心能量代谢与后燃卡片 (ACSM + EPOC 模型) */}
      <Card className="p-4 space-y-3 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-bold text-tone-ink text-[15px] font-headline">
            <span>🔥 能量代谢与后燃消耗</span>
          </div>
          <RiskTag tone="good">ACSM 标准</RiskTag>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Stat
            label="单场总消耗"
            value={`${metabolism.totalCalorieBurn}`}
            unit="kcal"
            sub="含 EPOC 后燃"
          />
          <Stat
            label="24h 后燃"
            value={`+${metabolism.epocCalorieBurn}`}
            unit="kcal"
            sub="肌蛋白修复"
          />
          <Stat
            label="等效纯脂肪"
            value={`≈${metabolism.equivalentFatBurnGrams}`}
            unit="g"
            sub="纯脂肪储备"
          />
        </div>

        <div className="p-2.5 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] text-[12px] text-tone-soft flex items-center justify-between font-caption border border-tone-border/40">
          <span>🏃 等效慢跑（8 km/h）：<strong className="text-tone-ink font-bold">{metabolism.equivalentJoggingMinutes}</strong> 分钟</span>
          <span>总容量：<strong className="text-tone-ink font-bold">{metabolism.totalVolumeKg}</strong> kg</span>
        </div>
      </Card>

      {/* 增肌与高刺激有效组数分析 */}
      <Card className="p-4 space-y-3 border border-tone-border">
        <div className="flex items-center justify-between">
          <div className="font-bold text-tone-ink text-[15px] font-headline">
            💪 肌群刺激与有效组数
          </div>
          <span className="text-[12px] font-bold text-tone-accent font-caption">
            高刺激组 {metabolism.effectiveHypertrophySets}/{doneSets} 组
          </span>
        </div>

        <div className="space-y-1.5">
          {metabolism.muscleStimulus.map((m) => (
            <div
              key={m.muscle}
              className="flex items-center justify-between p-2.5 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] text-xs border border-tone-border/40"
            >
              <span className="font-bold text-tone-ink">{m.muscle}</span>
              <span className="text-tone-soft font-semibold tabular-nums font-caption">
                有效刺激 <strong className="text-tone-good font-bold">{m.effectiveSets}</strong> 组 · 做功容量 {m.totalVolumeKg} kg
              </span>
            </div>
          ))}
        </div>

        <div className="p-3 rounded-elem bg-tone-accentSoft/60 border border-tone-accent/20 text-xs text-tone-ink leading-relaxed font-body">
          ⚡ <strong>增肌黄金窗口</strong>：阻抗训练已启动肌肉蛋白质合成（MPS），建议今日摄入蛋白质 <strong className="text-tone-accent font-bold">{metabolism.proteinTargetGrams} g</strong> 以促进肌纤维超量恢复。
        </div>
      </Card>

      {/* 全天能量平衡与减脂成效闭环 */}
      <Card className="p-4 space-y-2.5 border border-tone-border">
        <div className="flex items-center justify-between">
          <div className="font-bold text-tone-ink text-[15px] font-headline">
            🎯 全天能量平衡与减脂状态
          </div>
          <RiskTag tone={dailyBalance.status === "fat_loss" ? "good" : "accent"}>
            {dailyBalance.status === "fat_loss" ? "优质减脂状态" : "能量平衡状态"}
          </RiskTag>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2.5 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] border border-tone-border/40">
            <div className="text-tone-soft font-caption">全天总消耗 (TDEE)</div>
            <div className="text-[18px] font-bold text-tone-ink mt-0.5 tabular-nums font-display">
              {dailyBalance.tdee} <span className="text-xs font-normal text-tone-soft">kcal</span>
            </div>
          </div>
          <div className="p-2.5 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] border border-tone-border/40">
            <div className="text-tone-soft font-caption">今日净能量差额</div>
            <div className={`text-[18px] font-bold mt-0.5 tabular-nums font-display ${
              dailyBalance.netDeficitCalories >= 0 ? "text-tone-good" : "text-tone-warn"
            }`}>
              {dailyBalance.netDeficitCalories >= 0
                ? `-${dailyBalance.netDeficitCalories} kcal (赤字)`
                : `+${Math.abs(dailyBalance.netDeficitCalories)} kcal (盈余)`}
            </div>
          </div>
        </div>

        <p className="text-xs text-tone-soft leading-relaxed font-caption">
          {dailyBalance.adviceSummary}
        </p>
      </Card>

      {/* 各动作表现 + 规则引擎进阶建议 */}
      <div className="space-y-2 pt-1">
        <div className="flex items-center justify-between px-1">
          <span className="text-[14px] font-bold text-tone-ink font-headline">⚡ 双进阶建议（下次基准）</span>
          {verdicts.length > 1 && !allSaved && (
            <button
              type="button"
              onClick={handleBatchAccept}
              className="text-[12px] font-bold text-tone-accent hover:underline active:scale-95 font-caption"
            >
              一键采纳全部建议
            </button>
          )}
        </div>

        <Card className="divide-y divide-tone-border border border-tone-border overflow-hidden">
          {verdicts.map(({ e, verdict, volume }) => {
            const ex = EXERCISE_BY_ID.get(e.exerciseId);
            const tag = DECISION_TAG[verdict.decision];
            const saved = savedIds.has(e.exerciseId);
            return (
              <div key={e.exerciseId} className="p-4 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-bold text-tone-ink">{ex?.nameZh ?? e.exerciseId}</span>
                  <RiskTag tone={tag.tone}>{tag.label}</RiskTag>
                </div>
                <div className="text-[12px] font-medium text-tone-soft font-caption">
                  完成 {e.sets.filter((s) => s.completed).length}/{e.targetSets} 组 · 口径：{scopeShortLabel(e.weightUnitScope)}
                  {volume != null ? ` · 估算总容量 ≈ ${formatKg(volume)} kg` : null}
                </div>

                {/* 进阶决策大字提示 */}
                {verdict.suggestedWeightKg != null ? (
                  <div className={`p-2.5 rounded-elem text-xs font-bold flex items-center justify-between ${
                    verdict.decision === "increase"
                      ? "bg-tone-good/15 text-tone-good border border-tone-good/30"
                      : "bg-tone-accentSoft text-tone-accent border border-tone-accent/20"
                  }`}>
                    <span>
                      {verdict.decision === "increase"
                        ? `🎉 达成满次！建议下次加重为 ${formatKg(verdict.suggestedWeightKg)} kg`
                        : `🎯 保持 ${formatKg(verdict.suggestedWeightKg)} kg 继续冲刺目标次数`}
                    </span>
                    <span className="text-[11px] font-normal opacity-80">{scopeShortLabel(verdict.weightUnitScope)}</span>
                  </div>
                ) : null}

                <p className="text-[12px] text-tone-soft leading-relaxed font-caption">
                  依据：{verdict.humanReadableReason}
                </p>

                {saved ? (
                  <div className="p-2 rounded-elem bg-tone-good/10 text-tone-good text-xs font-bold flex items-center gap-1.5">
                    <span>✓</span>
                    <span>已采纳（自动保存为下次基准）</span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="primary"
                      className="flex-1 min-h-[42px] text-xs font-bold rounded-elem"
                      disabled={!store.sessionId}
                      onClick={() => void saveVerdict(e.exerciseId, verdict)}
                    >
                      接受建议
                    </Button>
                    <div className="flex gap-1.5 flex-1 min-w-[160px]">
                      <input
                        type="number"
                        inputMode="decimal"
                        placeholder="自定义重量"
                        aria-label={`${ex?.nameZh ?? e.exerciseId} 覆盖重量`}
                        value={overrideInput[e.exerciseId] ?? ""}
                        onChange={(ev) => setOverrideInput((m) => ({ ...m, [e.exerciseId]: ev.target.value }))}
                        className="flex-1 min-h-[42px] px-3.5 rounded-elem bg-black/5 dark:bg-white/10 text-xs font-semibold text-tone-ink min-w-0"
                      />
                      {overrideInput[e.exerciseId] ? (
                        <Button
                          variant="secondary"
                          className="min-h-[42px] px-3.5 text-xs font-bold rounded-elem"
                          disabled={!store.sessionId}
                          onClick={() => void saveVerdict(e.exerciseId, verdict, overrideInput[e.exerciseId])}
                        >
                          保存
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      </div>

      {/* 训练后状态反馈 */}
      <Card className="p-4 space-y-3 border border-tone-border">
        <div className="text-[15px] font-bold text-tone-ink font-headline">本次体感反馈（可选）</div>
        <div>
          <div className="text-[12px] font-medium text-tone-soft mb-1 font-caption">整体训练负荷</div>
          <Segmented value={difficulty} onChange={setDifficulty} options={DIFF_OPTIONS} />
        </div>
        <div>
          <div className="text-[12px] font-medium text-tone-soft mb-1 font-caption">足部状态</div>
          <Segmented<FootStatus>
            value={foot}
            onChange={setFoot}
            tone={foot === "significantly_worse" ? "danger" : foot === "slightly_worse" ? "warn" : "good"}
            options={[
              { value: "stable", label: "稳定正常" },
              { value: "slightly_worse", label: "轻微不适" },
              { value: "significantly_worse", label: "明显变差" },
            ]}
          />
        </div>
      </Card>

      <Button
        className="w-full min-h-[54px] text-[16px] font-bold shadow-sm"
        onClick={() => {
          setLastReadiness(null);
          nav("/");
        }}
      >
        完成并返回主页
      </Button>
    </div>
  );
}